import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  postStripeBillingPortalSessionUrlByAdvisorIdRequest,
  SubscriptionState,
} from '@client/model/subscriptionSeatsModel';
import { postStripeSubscriptionCheckoutSessionUrlRequest } from '@client/model/stripeSubscribeSessionModel';
import {
  selectSubscriptionSeatsState,
  selectStripeSubscribeSessionState,
  selectSession,
} from '@client/model/selectors';
import { STRIPE_BILLING_URL } from '@client/util/stripe/urls';

interface UseSubscriptionActionsProps {
  subscription: SubscriptionState;
}

export function useSubscriptionActions({ subscription }: UseSubscriptionActionsProps) {
  const dispatch = useDispatch();
  const { targetAdvisorId, billingPortal } = useSelector(selectSubscriptionSeatsState);
  const stripeSubscribeSession = useSelector(selectStripeSubscribeSessionState);
  const { advisor, auth0Session } = useSelector(selectSession);
  const priceId = auth0Session?.advisorUser?.appMetadata?.price_id_latest;

  // Handle billing portal redirect or fallback to static URL on error
  useEffect(() => {
    if (billingPortal.status === 'success' && billingPortal.url) {
      window.location.href = billingPortal.url;
    } else if (billingPortal.status === 'error') {
      // Fallback to static billing URL if dynamic portal fails
      window.open(STRIPE_BILLING_URL, '_blank', 'noopener,noreferrer');
    }
  }, [billingPortal.status, billingPortal.url]);

  // Handle subscription checkout redirect
  useEffect(() => {
    const { isLoading, success, url } = stripeSubscribeSession;

    if (!isLoading && success && url) {
      window.location.href = url;
    }
  }, [stripeSubscribeSession]);

  // Handle manage billing button click
  const handleManageBilling = useCallback(() => {
    if (targetAdvisorId && subscription?.stripeSubscriptionId) {
      dispatch(
        postStripeBillingPortalSessionUrlByAdvisorIdRequest({
          advisorId: targetAdvisorId,
          stripeSubscriptionId: subscription.stripeSubscriptionId,
        }),
      );
    }
  }, [dispatch, targetAdvisorId, subscription?.stripeSubscriptionId]);

  // Handle create new subscription
  const handleCreateSubscription = useCallback(() => {
    dispatch(
      postStripeSubscriptionCheckoutSessionUrlRequest({
        advisorId: advisor?.advisorId ?? '',
        priceId,
      }),
    );
  }, [advisor?.advisorId, dispatch, priceId]);

  const isLoadingBillingPortal = billingPortal.status === 'loading';
  const isLoadingCheckout = stripeSubscribeSession.isLoading;

  return {
    handleManageBilling,
    handleCreateSubscription,
    isLoadingBillingPortal,
    isLoadingCheckout,
  };
}
